import { CatalogMeasurementBoundary } from '../context/CompanyMeasurementProvider'
import { parseMeasurementUnit, convertMeasurement } from '../lib/measurement'
import React, { useEffect, useId, useRef, useState } from 'react'
import { useUnsavedChangesWarning } from '../hooks/useUnsavedChangesWarning'
import { createPortal } from 'react-dom'
import { Product } from '../services/product.service'
import { applyRotationPreset } from '../lib/rotationHelpers'
import { getPalletPresetId, palletPresetPatch, resolvePalletSpecifications } from '../lib/palletPresets'
import { fillMissingProductCenterOfGravity } from '../lib/productCenterOfGravity'
import ProductRotation3DPreview from './optimization/ProductRotation3DPreview'
import ProductDimensionsPanel from './ProductDimensionsPanel'
import ProductStackingPanel from './ProductStackingPanel'
import ProductPalletizationPanel from './ProductPalletizationPanel'
import StarRating from './ui/StarRating'
import { Layers, Rotate3D, Package, Ruler, Settings, Table } from 'lucide-react'
import { ArrowPathIcon, XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'

// Small helper components duplicated from ProductSettings for now
function Toggle({ id, checked, onChange, label, description, disabled }: {
  id: string
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  description?: string
  disabled?: boolean
}) {
  return (
    <label htmlFor={id} className={`w-full flex items-center justify-between py-1.5 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
      <div>
        <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{label}</span>
        {description && <p className="text-xs mt-0.5" style={{ color: 'var(--faint)' }}>{description}</p>}
      </div>
      <div className="relative flex-shrink-0">
        <input
          type="checkbox"
          id={id}
          checked={checked}
          disabled={disabled}
          onChange={(e) => !disabled && onChange(e.target.checked)}
          className="sr-only"
        />
        <div className={`w-9 h-5 rounded-full relative transition-colors ${checked ? '' : ''}`} style={{ background: checked ? 'var(--accent)' : 'var(--border)' }}>
          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow-sm`} style={{ left: checked ? '18px' : '2px' }} />
        </div>
      </div>
    </label>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--muted)' }}>{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs" style={{ color: 'var(--faint)' }}>{hint}</p>}
    </div>
  )
}

function RangeSlider({
  min,
  max,
  onChange,
  step = 0.1,
  minLimit = -50,
  maxLimit = 50
}: {
  min: number | string | null
  max: number | string | null
  onChange: (min: number | null, max: number | null) => void
  step?: number
  minLimit?: number
  maxLimit?: number
}) {
  const parseValue = (value: number | string | null): number | null => {
    if (typeof value === 'number' && !isNaN(value)) return value
    if (typeof value === 'string') {
      const parsed = parseFloat(value)
      return Number.isNaN(parsed) ? null : parsed
    }
    return null
  }

  const clamp = (value: number, minVal: number, maxVal: number) => Math.min(maxVal, Math.max(minVal, value))

  const safeMinVal = clamp(parseValue(min) ?? minLimit, minLimit, maxLimit)
  const safeMaxVal = clamp(parseValue(max) ?? maxLimit, minLimit, maxLimit)

  const percentageMin = ((safeMinVal - minLimit) / (maxLimit - minLimit)) * 100
  const percentageMax = ((safeMaxVal - minLimit) / (maxLimit - minLimit)) * 100

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = parseFloat(e.target.value)
    const percentage = ((newVal - minLimit) / (maxLimit - minLimit)) * 100

    const midPoint = (percentageMin + percentageMax) / 2
    if (percentage < midPoint) {
      if (newVal <= safeMaxVal) {
        onChange(newVal, parseValue(max))
      }
    } else {
      if (newVal >= safeMinVal) {
        onChange(parseValue(min), newVal)
      }
    }
  }

  return (
    <div className="relative py-2">
      <div className="relative h-2 rounded-full" style={{ background: 'var(--border)' }}>
        <div
          className="absolute h-full rounded-full"
          style={{
            left: `${percentageMin}%`,
            right: `${100 - percentageMax}%`,
            background: 'var(--accent)'
          }}
        />
      </div>

      <input
        type="range"
        min={minLimit}
        max={maxLimit}
        step={step}
        value={safeMinVal}
        onChange={handleChange}
        className="absolute w-full h-8 opacity-0 cursor-pointer z-20"
        style={{ top: '-0.5rem' }}
      />

      <div
        className="absolute w-5 h-5 rounded-full border-2 shadow cursor-pointer hover:scale-110 transition-transform z-30 pointer-events-none"
        style={{
          left: `calc(${percentageMin}% - 10px)`,
          top: '0.25rem',
          background: 'var(--paper)',
          borderColor: 'var(--accent)'
        }}
      />

      <div
        className="absolute w-5 h-5 rounded-full border-2 shadow cursor-pointer hover:scale-110 transition-transform z-30 pointer-events-none"
        style={{
          left: `calc(${percentageMax}% - 10px)`,
          top: '0.25rem',
          background: 'var(--paper)',
          borderColor: 'var(--accent)'
        }}
      />

      <div className="flex justify-between mt-4 text-xs font-medium" style={{ color: 'var(--text)' }}>
        <span>{safeMinVal.toFixed(1)}°</span>
        <span>{safeMaxVal.toFixed(1)}°</span>
      </div>
    </div>
  )
}

const inputCls = "w-full px-3.5 py-2.5 rounded-xl text-sm outline-none mono"
const inputStyle = { background: "var(--paper)", border: "1px solid var(--border)", color: "var(--text)" }
const selectCls = "w-full px-3.5 py-2.5 rounded-xl text-sm outline-none mono"
const selectStyle = { background: "var(--paper)", border: "1px solid var(--border)", color: "var(--text)" }

const TABS = [
  { id: 'general', label: 'Général', icon: Package },
  { id: 'dimensions', label: 'Dimensions & Poids', icon: Ruler },
  { id: 'optimization', label: 'Empilement', icon: Layers },
  { id: 'rotation', label: 'Rotation', icon: Rotate3D },
  { id: 'palletization', label: 'Palettisation', icon: Table },
  { id: 'special', label: 'Spécifications', icon: Settings },
]

export default function ProductForm({ portal = false, ...props }: React.ComponentProps<typeof ProductFormContent> & { portal?: boolean }) {
  const content = <ProductFormContent key={props.product?.id || 'new'} {...props} />
  return (
    <CatalogMeasurementBoundary>
      {portal && typeof document !== 'undefined' ? createPortal(content, document.body) : content}
    </CatalogMeasurementBoundary>
  )
}

function ProductFormContent({ product, onSave, onClose, isLoading, adrClasses, unNumbers, readOnly = false }: {
  product: Product | null
  onSave: (product: Partial<Product>) => void
  onClose: () => void
  isLoading: boolean
  adrClasses: any[]
  unNumbers: any[]
  readOnly?: boolean
}) {
  const [activeTab, setActiveTab] = useState('general')
  const dimensionInputId = useId()
  const formId = useId()
  const navigationRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const navigation = navigationRef.current
    if (!navigation) return
    const revealStep = () => {
      const current = navigation.querySelector<HTMLElement>('[aria-current="step"]')
      if (!current || navigation.scrollWidth <= navigation.clientWidth) return
      const offset = current.getBoundingClientRect().left - navigation.getBoundingClientRect().left + navigation.scrollLeft
      navigation.scrollLeft = offset - (navigation.clientWidth - current.offsetWidth) / 2
    }
    revealStep()
    const observer = new ResizeObserver(revealStep)
    observer.observe(navigation)
    return () => observer.disconnect()
  }, [activeTab])
  const dimensionToFocus = useRef<'length' | 'width' | 'height' | null>(null)
  useEffect(() => {
    if (activeTab === 'dimensions' && dimensionToFocus.current) {
      document.getElementById(`${dimensionInputId}-${dimensionToFocus.current}`)?.focus()
      dimensionToFocus.current = null
    }
  }, [activeTab, dimensionInputId])
  const [palletChoice, setPalletChoice] = useState(() => getPalletPresetId(product))
  const [formData, setFormData] = useState<Partial<Product>>(() => fillMissingProductCenterOfGravity(product || {
    name: '', description: '', sku: '', hs_code: '',
    unit_of_measure: 'PIECE', weight: null, weight_unit: 'kg',
    length: null, width: null, height: null, dimension_unit: 'mm',
    volume: null, volume_unit: 'm3', packaging_type: '',
    is_dangerous_goods: false, dangerous_goods_class: '', un_number: '',
    temperature_controlled: false, min_temperature: null, max_temperature: null,
    temperature_unit: 'C', country_of_origin: '', status: 'active',
    min_support_ratio: null, quantity: 1, stackable: true, fragile: false,
    rotatable: true, no_stack_above: false, max_stack_weight: null,
    max_stack_layer: null, color: '#6366f1',
    must_be_on_floor: false, requires_pallet: false, pallet_type: 'EUR',
    max_pallet_height_mm: null, max_items_per_pallet: null, pallet_board_height_mm: null, pallet_empty_weight_kg: null, pallet_color: null, max_pallet_weight_kg: null,
    allow_rotation_x: true, allow_rotation_y: true, allow_rotation_z: true,
    rotation_strategy: 'DEFAULT_6_ROTATIONS',
    favorite_rating: 0
  }))

  const [initialForm] = useState(() => JSON.stringify(formData))
  useUnsavedChangesWarning(!readOnly && (isLoading || JSON.stringify(formData) !== (product ? JSON.stringify(fillMissingProductCenterOfGravity(product)) : initialForm)))
  const set = (patch: Partial<Product>) => setFormData(f => fillMissingProductCenterOfGravity({ ...f, ...patch }))
  const dimensionUnit = formData.dimension_unit || 'mm'
  const palletSpecifications = resolvePalletSpecifications(formData, palletChoice)
  const [saveError, setSaveError] = useState('')
  const saveProduct = () => {
    for (const [axis, dimension] of [['x', 'length'], ['y', 'width'], ['z', 'height']] as const) {
      const value = formData[`center_of_gravity_${axis}`]
      if (value == null) continue
      const size = formData[dimension] == null ? NaN : convertMeasurement(Number(formData[dimension]), parseMeasurementUnit(dimensionUnit), 'mm')
      if (!Number.isFinite(Number(value)) || Number(value) < 0 || !Number.isFinite(size) || size <= 0 || Number(value) > size) {
        setSaveError(`Centre de gravité ${axis.toUpperCase()} : renseignez une dimension positive et une coordonnée comprise entre zéro et cette dimension.`)
        setActiveTab('dimensions')
        return
      }
    }
    setSaveError('')
    onSave({ ...formData, dimension_unit: dimensionUnit,
      ...(formData.requires_pallet ? {
        pallet_board_height_mm: palletSpecifications.boardHeightMm,
        pallet_empty_weight_kg: palletSpecifications.emptyWeightKg,
      } : {}),
    })
  }

  useEffect(() => {
    if (product) {
      setFormData(fillMissingProductCenterOfGravity(product))
      setPalletChoice(getPalletPresetId(product))
    }
  }, [product])

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        .product-form{
          --ink:#0F172A; --ink-soft:#334155;
          --paper:#F8FAFC; --surface:#FFFFFF;
          --border:#E2E8F0; --border-soft:#F1F5F9;
          --text:#0F172A; --muted:#64748B; --faint:#64748B;
          --accent:#F97316; --accent-soft:#FFF7ED; --accent-ink:#EA580C;
          --cold:#1E7FAE; --cold-soft:#E4F2F8;
          --danger:#C43D3D; --danger-soft:#FBE7E7;
          --success:#2F9E6B; --success-soft:#E5F6EE;
          --slate:#5B6472; --slate-soft:#EEF0F3;
        }
        .disp{font-family:'Space Grotesk',sans-serif;}
        .mono{font-family:'IBM Plex Mono',monospace;}
        .sans{font-family:'Inter',system-ui,sans-serif;}
        .scrollbar-thin::-webkit-scrollbar{width:6px;height:6px;}
        .scrollbar-thin::-webkit-scrollbar-thumb{background:#D7D9DF;border-radius:3px;}
      `}</style>
      <div className={`product-form sans fixed inset-0 ${readOnly ? 'z-[200]' : 'z-50'} flex items-center justify-center bg-slate-900/40 p-3 backdrop-blur-sm sm:p-5`}>
        <div role="dialog" aria-modal="true" aria-labelledby={`${formId}-title`} className="flex w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl sm:flex-row" style={{ maxHeight: '92dvh' }}>
          <aside className="shrink-0 border-b border-slate-200 bg-white p-3 sm:w-52 sm:border-b-0 sm:border-r sm:px-3 sm:py-6">
            <div className="mb-7 hidden items-center gap-2.5 px-3 sm:flex">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-orange-600"><Package size={21} /></span>
              <div><p className="text-sm font-semibold text-slate-900">Fiche produit</p><p className="mt-0.5 text-xs text-slate-500">{readOnly ? 'Consultation' : product ? 'Modification' : 'Création'}</p></div>
            </div>
            <nav ref={navigationRef} aria-label="Sections de la fiche produit" className="flex gap-1 overflow-x-auto sm:flex-col sm:overflow-visible">
              {TABS.map(tab => {
                const Icon = tab.icon
                const active = activeTab === tab.id
                return (
                  <button
                    key={tab.id}
                    type="button"
                    aria-current={active ? 'step' : undefined}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-xl border-l-[3px] px-3 py-3 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/30 sm:w-full ${active ? 'border-orange-500 bg-orange-50 font-semibold text-orange-600' : 'border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                )
              })}
            </nav>
          </aside>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-slate-50">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-4 sm:px-6 sm:py-5">
              <div className="min-w-0">
                <div className="mb-1.5 inline-flex rounded-full bg-orange-50 px-2.5 py-1 text-[10px] font-semibold text-orange-600">Étape {TABS.findIndex(t => t.id === activeTab) + 1} sur {TABS.length}</div>
                <h2 id={`${formId}-title`} className="text-xl font-semibold tracking-tight text-slate-900">
                  {TABS.find(t => t.id === activeTab)?.label}
                </h2>
              </div>
              <button type="button" aria-label="Fermer la fiche produit" onClick={onClose} className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/30">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form id={formId} onSubmit={e => { e.preventDefault(); if (!readOnly) saveProduct() }} className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
              {saveError && <p role="alert" className="px-6 pt-3 text-red-600">{saveError}</p>}
              <fieldset disabled={readOnly} className="min-w-0 border-0 p-0">
              <div className="space-y-5 p-4 sm:p-5">

                {/* ── TAB: GÉNÉRAL ── */}
                {activeTab === 'general' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Field label="Nom du produit *">
                          <input required type="text" value={formData.name || ''} onChange={e => set({ name: e.target.value })}
                            placeholder="Ex: Palette Europe standard"
                            className={inputCls} style={inputStyle} />
                        </Field>
                      </div>
                      <div className="md:col-span-2">
                        <Field label="Description">
                          <textarea value={formData.description || ''} onChange={e => set({ description: e.target.value })}
                            rows={2} placeholder="Description optionnelle..." className={inputCls} style={inputStyle} />
                        </Field>
                      </div>
                      <Field label="SKU / Référence">
                        <input type="text" value={formData.sku || ''} onChange={e => set({ sku: e.target.value })}
                          placeholder="PROD-001" className={inputCls} style={inputStyle} />
                      </Field>
                      <Field label="Code HS (douane)">
                        <input type="text" value={formData.hs_code || ''} onChange={e => set({ hs_code: e.target.value })}
                          placeholder="Ex: 4415.20" className={inputCls} style={inputStyle} />
                      </Field>
                      <Field label="Quantité par défaut">
                        <input type="number" min="1" value={formData.quantity || 1}
                          onChange={e => set({ quantity: parseInt(e.target.value) || 1 })} className={inputCls} style={inputStyle} />
                      </Field>
                      <Field label="Statut">
                        <select value={formData.status || 'active'} onChange={e => set({ status: e.target.value })} className={selectCls} style={selectStyle}>
                          <option value="active">Actif</option>
                          <option value="inactive">Inactif</option>
                          <option value="draft">Brouillon</option>
                        </select>
                      </Field>
                      <div className="md:col-span-2">
                        <Field label="Niveau de favoris" hint="Sélectionnez 1-5 étoiles pour marquer ce produit comme favori (apparaîtra dans les presets de cargaison)">
                          <StarRating
                            rating={formData.favorite_rating || 0}
                            onChange={(rating) => set({ favorite_rating: rating })}
                            size="md"
                          />
                        </Field>
                      </div>
                    </div>

                    {/* Color picker premium */}
                    <div className="rounded-xl p-4" style={{ background: 'var(--paper)', border: '1px solid var(--border)' }}>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                        Couleur du produit (visualisation 3D)
                      </label>
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <input type="color" value={formData.color || '#6366f1'} onChange={e => set({ color: e.target.value })}
                            className="w-14 h-14 rounded-xl border-2 border-white dark:border-gray-700 cursor-pointer shadow-md p-1 bg-transparent" />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {['#6366f1', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#64748b'].map(c => (
                            <button key={c} type="button" onClick={() => set({ color: c })}
                              className={`w-7 h-7 rounded-lg shadow cursor-pointer hover:scale-110 transition-transform ${formData.color === c ? 'ring-2 ring-offset-2 ring-blue-500 ring-offset-white dark:ring-offset-gray-800' : ''}`}
                              style={{ backgroundColor: c }} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* ── TAB: DIMENSIONS ── */}
                {activeTab === 'dimensions' && <ProductDimensionsPanel product={formData} onChange={set} dimensionInputId={dimensionInputId} />}

                {/* ── TAB: OPTIMISATION 3D ── */}
                {activeTab === 'optimization' && <ProductStackingPanel product={formData} onChange={set}
                  onCompleteDimensions={() => {
                    dimensionToFocus.current = (['length', 'width', 'height'] as const).find(key => formData[key] == null || !Number.isFinite(Number(formData[key])) || Number(formData[key]) <= 0) || 'length'
                    setActiveTab('dimensions')
                  }} />}

                {/* ── TAB: OPTIONS DE ROTATION ── */}
                {activeTab === 'rotation' && (
                  <>
                    <div className="grid grid-cols-1 gap-4">
                      <Toggle id="rotatable" checked={formData.rotatable ?? true} onChange={v => {
                        if (!v) {
                          set({ rotatable: false, rotation_strategy: 'NO_STACKING', allow_rotation_x: false, allow_rotation_y: false, allow_rotation_z: false })
                        } else {
                          set({ rotatable: true, rotation_strategy: 'DEFAULT_6_ROTATIONS', allow_rotation_x: true, allow_rotation_y: true, allow_rotation_z: true })
                        }
                      }}
                        label="Rotation autorisée" description="Peut être tourné lors du chargement" />
                    </div>

                    <div className={`rounded-xl border p-4 mt-4 transition-colors ${formData.rotatable ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50' : 'border-amber-200 dark:border-amber-900/50 bg-amber-50/20 dark:bg-amber-950/10 opacity-75'}`}>
                      {!formData.rotatable && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mb-3">
                          Rotation désactivée - La stratégie est forcée à 'Sans rotation' avec une orientation fixe.
                        </p>
                      )}
                      <div className="space-y-4">
                        <Field label="Stratégie de rotation">
                          <select
                            disabled={!formData.rotatable}
                            value={!formData.rotatable ? 'NO_STACKING' : (formData.rotation_strategy || 'DEFAULT_6_ROTATIONS')}
                            onChange={e => {
                              const strategy = e.target.value
                              if (strategy === 'CUSTOM') {
                                set({ rotation_strategy: 'CUSTOM' })
                              } else {
                                const axes = applyRotationPreset(strategy)
                                set({
                                  rotation_strategy: strategy,
                                  allow_rotation_x: axes.allowRotationX,
                                  allow_rotation_y: axes.allowRotationY,
                                  allow_rotation_z: axes.allowRotationZ,
                                })
                              }
                            }}
                            className={selectCls} style={selectStyle}
                          >
                            <option value="DEFAULT_6_ROTATIONS">Par défaut (6 rotations)</option>
                            <option value="DEFAULT_FLAT_ROTATIONS">Rotation a plat</option>
                            <option value="ADR_RESTRICTED">ADR restreint (2 rotations)</option>
                            <option value="NO_STACKING">Sans rotation (1 orientation)</option>
                            <option value="CUSTOM">Personnalisé</option>
                          </select>
                        </Field>

                        <ProductRotation3DPreview
                          length={formData.length ?? null}
                          width={formData.width ?? null}
                          height={formData.height ?? null}
                          unit={dimensionUnit}
                          color={formData.color || '#6366f1'}
                          rotatable={formData.rotatable ?? true}
                          allowRotationX={formData.allow_rotation_x ?? true}
                          allowRotationY={formData.allow_rotation_y ?? true}
                          allowRotationZ={formData.allow_rotation_z ?? true}
                          onToggleAxis={(axisKey, val) => {
                            set({
                              [axisKey]: val,
                              rotation_strategy: 'CUSTOM'
                            })
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* ── TAB: PALETTISATION ── */}
                {activeTab === 'palletization' && <ProductPalletizationPanel product={formData} palletChoice={palletChoice} onChange={set}
                  onPresetChange={choice => { setPalletChoice(choice); set(palletPresetPatch(choice)) }}
                  onCompleteDimensions={readOnly ? undefined : () => {
                    dimensionToFocus.current = (['length', 'width', 'height'] as const).find(key => formData[key] == null || !Number.isFinite(Number(formData[key])) || Number(formData[key]) <= 0) || 'length'
                    setActiveTab('dimensions')
                  }} />}

                {/* ── TAB: SPÉCIFICATIONS ── */}
                {activeTab === 'special' && (
                  <>
                    <div className={`rounded-xl p-4 transition-colors ${formData.is_dangerous_goods ? '' : ''}`} style={{ background: formData.is_dangerous_goods ? 'var(--danger-soft)' : 'var(--paper)', border: formData.is_dangerous_goods ? '1px solid var(--danger)' : '1px solid var(--border)' }}>
                      <Toggle id="is_dangerous_goods" checked={formData.is_dangerous_goods ?? false} onChange={v => set({ is_dangerous_goods: v })}
                        label="Produit dangereux" description="Soumettre une classification ADR" />
                      {formData.is_dangerous_goods && (
                        <div className="mt-4 pt-4 space-y-4" style={{ borderTop: '1px solid var(--border)' }}>
                          <div className="flex gap-3 rounded-lg p-3" style={{ background: 'var(--paper)', border: '1px solid var(--danger)' }}>
                            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" style={{ color: 'var(--danger)' }} />
                            <div>
                              <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
                                Strategie moteur: GRAVITY_AWARE
                              </p>
                              <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                                Le moteur privilegie les positions basses et stables afin de reduire le centre de gravite et les risques pendant le transport.
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Field label="Classe de danger">
                            <select
                              value={formData.dangerous_goods_class || ''}
                              onChange={e => {
                                const selectedClass = e.target.value
                                set({ dangerous_goods_class: selectedClass })
                                set({ un_number: '' })
                              }}
                              className={inputCls}
                              style={inputStyle}
                            >
                              <option value="">Sélectionner une classe</option>
                              {adrClasses.map(cls => (
                                <option key={cls.id} value={cls.code}>
                                  {cls.code} — {cls.metadata.label_short || cls.label}
                                </option>
                              ))}
                            </select>
                          </Field>
                          <Field label="Numéro UN">
                            <select
                              value={formData.un_number || ''}
                              onChange={e => set({ un_number: e.target.value })}
                              className={inputCls}
                              style={inputStyle}
                              disabled={!formData.dangerous_goods_class}
                            >
                              <option value="">Sélectionner un numéro UN</option>
                              {unNumbers
                                .filter(un => un.metadata.adr_class === formData.dangerous_goods_class)
                                .map(un => (
                                  <option key={un.id} value={un.code}>
                                    {un.code} — {un.label}
                                  </option>
                                ))}
                            </select>
                          </Field>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className={`rounded-xl p-4 transition-colors ${formData.temperature_controlled ? '' : ''}`} style={{ background: formData.temperature_controlled ? 'var(--cold-soft)' : 'var(--paper)', border: formData.temperature_controlled ? '1px solid var(--cold)' : '1px solid var(--border)' }}>
                      <Toggle id="temperature_controlled" checked={formData.temperature_controlled ?? false} onChange={v => set({ temperature_controlled: v })}
                        label="Produit frigorifique" description="Nécessite une chaîne du froid contrôlée" />
                      {formData.temperature_controlled && (
                        <div className="grid grid-cols-2 gap-4 mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                          <Field label="Plage de température">
                            <RangeSlider
                              min={formData.min_temperature ?? null}
                              max={formData.max_temperature ?? null}
                              onChange={(min, max) => set({ min_temperature: min, max_temperature: max })}
                              step={0.1}
                              minLimit={-50}
                              maxLimit={50}
                            />
                          </Field>
<Field label="Unité">
                            <select value={formData.temperature_unit || 'C'} onChange={e => set({ temperature_unit: e.target.value })} className={selectCls} style={selectStyle}>
                              <option value="C">Celsius (°C)</option>
                              <option value="F">Fahrenheit (°F)</option>
                              <option value="K">Kelvin (K)</option>
                            </select>
                          </Field>

                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    </div>
                  </>
                )}
              </div>
              </fieldset>
            </form>

              <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3.5 sm:px-6">
                <div className="flex items-center gap-1.5">
                  {TABS.map((t) => (
                    <span key={t.id} className="w-1.5 h-1.5 rounded-full" style={{ background: t.id === activeTab ? 'var(--accent)' : 'var(--border)' }} />
                  ))}
                </div>
                <div className="flex items-center gap-2.5">
                  <button type="button" onClick={onClose} className="rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/30">
                    {readOnly ? 'Fermer' : 'Annuler'}
                  </button>
                  {!readOnly && <button type="submit" form={formId} disabled={isLoading}
                    className="flex items-center gap-1.5 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-orange-500/20 transition hover:bg-orange-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 disabled:opacity-50">
                    {isLoading ? (
                      <><ArrowPathIcon className="w-4 h-4 animate-spin" />Sauvegarde...</>
                    ) : (
                      <><CheckCircleIcon className="w-4 h-4" />Sauvegarder</>
                    )}
                  </button>}
                </div>
              </div>
          </div>
        </div>
      </div>
    </>
  )
}
